import { useCallback, useMemo, useState } from "react";
import type { ActivityOutcomeKind } from "@common/schemas/domains/activity/activity-outcomes.schema";
import { ActivitySeries } from "@common/schemas/domains/activity/activity-timeline.schema";
import { OrganizationHistoryFilter } from "@common/types/auditLog";
import { ORGANIZATION_ACTIVITY_OUTCOMES } from "@components/activityTimeline/activityTimelineOutcomes.lib";
import type { StoredLastVisit } from "@components/activityTimeline/lastVisit/lastVisit.lib";
import { storedLastVisitSchema } from "@components/activityTimeline/lastVisit/lastVisit.lib";
import type { ActivityChartDensity } from "@components/activityTimeline/activityChartDensity.lib";
import { activityChartDensitySchema } from "@components/activityTimeline/activityChartDensity.lib";
import { useActivityTimelinePage } from "@components/activityTimeline/useActivityTimelinePage";
import {
    buildActivityFilters,
    excludeDisabledChannels,
    getActivitiesFromValues,
} from "@components/history/components/historyActivityFilters";
import { useIsChannelAttributionEnabled } from "@components/history/hooks/useIsChannelAttributionEnabled";
import {
    DateOnlyDateRange,
    toDateOnlyDateRange,
    toLocalDateRange,
} from "@design-system/DateRangeFilter/DateRangeFilterNext.lib";
import { removeTimeZoneOffset } from "@helpers/date";
import {
    ORGANIZATION_USER_CACHE_KEYS,
    useOrganizationUserCache,
} from "@hooks/useOrganizationUserCache";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { getRevertedFilterParams } from "@hooks/view/useHistoryPageFiltersCache";
import type {
    OrganizationActivityFilters,
    OrganizationActivitySelection,
} from "./organizationActivityFilters.types";
import { useOrganizationHistoryStart } from "./useOrganizationHistoryStart";

const TIMELINE_PICKABLE_SERIES: readonly ActivitySeries[] = [
    ActivitySeries.KICK,
];
const NO_SERIES: readonly ActivitySeries[] = [];
const NO_OUTCOMES: readonly ActivityOutcomeKind[] = [];

/** Search, the Filter popover's picks and Clients: what Clear filters resets, without the dates. */
function toPickedHistoryFilter(
    filters: OrganizationActivityFilters,
): OrganizationHistoryFilter {
    const result: OrganizationHistoryFilter = {
        ...getRevertedFilterParams(filters.revertedFilter),
    };

    if (filters.actorIds && filters.actorIds.length > 0) {
        result.actorIds = filters.actorIds;
    }
    if (filters.channels && filters.channels.length > 0) {
        result.channels = filters.channels;
    }
    const activityFilters = buildActivityFilters(
        getActivitiesFromValues(filters.activityValues),
        filters.changeTypes,
    );
    if (activityFilters) {
        result.activityFilters = activityFilters;
    }
    if (filters.changeTypes && filters.changeTypes.length > 0) {
        result.changeTypes = filters.changeTypes;
    }
    if (filters.clientWorkspaceIds && filters.clientWorkspaceIds.length > 0) {
        result.clientWorkspaceIds = filters.clientWorkspaceIds;
    }
    if (filters.search) {
        result.search = filters.search;
    }

    return result;
}

function toOrganizationHistoryFilter(
    filters: OrganizationActivityFilters,
): OrganizationHistoryFilter {
    const result = toPickedHistoryFilter(filters);

    if (filters.start) {
        result.minCreatedAt = removeTimeZoneOffset(filters.start);
    }
    if (filters.end) {
        result.maxCreatedAt = removeTimeZoneOffset(filters.end);
    }

    return result;
}

/**
 * The organization Activity page's filters, page and timeline. The
 * timeline's window is the date range. Series and outcome picks only
 * apply, and only show in the pickers, with the timeline. The dates are
 * not a filter: they never count as active and Clear filters keeps them.
 */
export function useOrganizationActivityFilters() {
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState<OrganizationActivityFilters>({});
    const [stored, setStored] = useOrganizationUserCache<StoredLastVisit>(
        ORGANIZATION_USER_CACHE_KEYS.ORGANIZATION_ACTIVITY_LAST_VISIT_CACHE_KEY,
        storedLastVisitSchema,
    );
    const [storedDensity, saveDensity] =
        useOrganizationUserCache<ActivityChartDensity>(
            ORGANIZATION_USER_CACHE_KEYS.ORGANIZATION_ACTIVITY_CHART_DENSITY_CACHE_KEY,
            activityChartDensitySchema,
        );

    const { agentRuns: isAgentRunsEnabled } = useTypedFlags();
    const isChannelFilterEnabled = useIsChannelAttributionEnabled();

    const dateRange = useMemo(
        () => toDateOnlyDateRange({ start: filters.start, end: filters.end }),
        [filters.start, filters.end],
    );
    const enabledFilters = useMemo<OrganizationActivityFilters>(
        () => ({
            ...filters,
            channels: isChannelFilterEnabled
                ? excludeDisabledChannels(filters.channels, {
                      isAgentRunsEnabled,
                  })
                : undefined,
        }),
        [filters, isChannelFilterEnabled, isAgentRunsEnabled],
    );
    const apiFilters = useMemo(
        () => toOrganizationHistoryFilter(enabledFilters),
        [enabledFilters],
    );
    const historyStart = useOrganizationHistoryStart(
        filters.clientWorkspaceIds,
    );
    const {
        isActivityTimelineEnabled,
        timeline,
        listFilter,
        hasActiveTimelineFilters,
        isSinceLastVisitWindow,
        previewWindow,
        setPreviewWindow,
        chartDensity,
    } = useActivityTimelinePage({
        dateRange,
        stored,
        save: setStored,
        storedDensity: { stored: storedDensity, save: saveDensity },
        historyStartAt: historyStart.startAt,
        apiFilters,
        outcome: filters.outcome,
        series: filters.series,
    });

    const selection = useMemo<OrganizationActivitySelection>(
        () => ({
            actorIds: filters.actorIds,
            channels: filters.channels,
            activityValues: filters.activityValues,
            activityFilters: filters.activityFilters,
            changeTypes: filters.changeTypes,
            revertedFilter: filters.revertedFilter,
            ...(isActivityTimelineEnabled && {
                series: filters.series,
                outcome: filters.outcome,
            }),
        }),
        [
            isActivityTimelineEnabled,
            filters.actorIds,
            filters.channels,
            filters.activityValues,
            filters.activityFilters,
            filters.changeTypes,
            filters.revertedFilter,
            filters.series,
            filters.outcome,
        ],
    );
    const pickableSeries = isActivityTimelineEnabled
        ? TIMELINE_PICKABLE_SERIES
        : NO_SERIES;
    const pickableOutcomes = isActivityTimelineEnabled
        ? ORGANIZATION_ACTIVITY_OUTCOMES
        : NO_OUTCOMES;

    const handleDateRangeChange = useCallback((range: DateOnlyDateRange) => {
        const localRange = toLocalDateRange(range);
        setFilters((prev) => ({
            ...prev,
            start: localRange.start,
            end: localRange.end,
        }));
        setPage(1);
    }, []);

    const handleSelectionChange = useCallback(
        (next: Partial<OrganizationActivityFilters>) => {
            setFilters((prev) => ({ ...prev, ...next }));
            setPage(1);
        },
        [],
    );

    const handleClientWorkspaceIdsChange = useCallback(
        (clientWorkspaceIds: string[] | undefined) => {
            setFilters((prev) => ({ ...prev, clientWorkspaceIds }));
            setPage(1);
        },
        [],
    );

    const handleClearFilters = useCallback(() => {
        setFilters(({ start, end }) => ({ start, end }));
        setPage(1);
    }, []);

    const hasActiveFilters =
        Object.keys(toPickedHistoryFilter(enabledFilters)).length > 0 ||
        hasActiveTimelineFilters;
    const hasPickedDates =
        filters.start !== undefined || filters.end !== undefined;

    return {
        page,
        setPage,
        filters,
        selection,
        pickableSeries,
        pickableOutcomes,
        isChannelFilterEnabled,
        dateRange,
        listFilter,
        timeline,
        isActivityTimelineEnabled,
        hasActiveFilters,
        hasPickedDates,
        isSinceLastVisitWindow,
        previewWindow,
        setPreviewWindow,
        chartDensity,
        historyStartClientName: historyStart.clientName,
        handleDateRangeChange,
        handleSelectionChange,
        handleClientWorkspaceIdsChange,
        handleClearFilters,
    };
}
